import { applyCreatureMonsterTemplate } from './from-base-creature-template';
import { createClassificationContext } from './from-base-classification-context';
import { constructPlayerDefinitionPictures } from './from-base-player-media';
import { readItemField } from './item-field-reader';
import { readLayerListEntryUuid, readItemUuidSlot } from './inventory-raw-readers';
import { compileCreatureDisplayName } from './creature-display-name';
import { SWARM_TEMPLATE_BY_ID, SWARM_TEMPLATES } from '../../shared/swarm-templates';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseInventoryMetadata } from './from-base-inventory';

/** A saved swarm state or its full-strength virtual row; compile never seeds it. */
export function constructCreatureSwarmState(source: Row, base: Row): Row {
    const row = fromBaseClone(source), ec = fromBaseRecord(row.encounter_creatures) ? row.encounter_creatures : null;
    const id = readItemField(ec, 'encounter_creature_id');
    if (!ec || !fromBaseUuid(id)) return row;
    // Native scans own carriers in both stores, never a provider's templates.
    const lists = [(row.item_layer_types as Row).creature_template, row.creature_template];
    const entries = lists.flatMap(value => value == null ? [] : value !== null && typeof value === 'object'
        && Object.prototype.hasOwnProperty.call(value, '0') ? Object.values(value) : [value]);
    const template = entries.map(entry => SWARM_TEMPLATE_BY_ID[readLayerListEntryUuid(entry) || readItemUuidSlot(entry)]).find(Boolean);
    if (!template) return row;
    if (!Array.isArray(base.encounter_creature_swarm_states)) throw new FromBaseInputMissing('swarm-state-select', id);
    const state = (base.encounter_creature_swarm_states as Row[]).find(state =>
        phpJsonString(readItemField(state, 'encounter_creature_swarm_state_encounter_creature_id')).toUpperCase() === id.toUpperCase()
        && phpJsonInt(readItemField(state, 'encounter_creature_swarm_state_deleted')) === 0);
    const members = state ? Math.max(0, phpJsonInt(readItemField(state, 'encounter_creature_swarm_state_members'))) : template.monsterQty;
    ec.swarm_members = members;
    ec.swarm_members_start = state ? Math.max(1, phpJsonInt(readItemField(state, 'encounter_creature_swarm_state_members_start'))) : template.monsterQty;
    ec.swarm_tier = members < 2 ? null : Object.values(SWARM_TEMPLATES).map(entry => entry.monsterQty)
        .filter(count => count <= members).sort((a, b) => b - a)[0] ?? 2;
    if (members >= 2) return row;
    for (const bucket of [row.item_layer_types as Row, row]) {
        const value = bucket.creature_template;
        if (value !== null && typeof value === 'object') {
            const entries = Object.prototype.hasOwnProperty.call(value, '0') ? Object.values(value) : [value];
            bucket.creature_template = entries.filter(entry =>
                (readLayerListEntryUuid(entry) || readItemUuidSlot(entry)).toUpperCase() !== template.id);
        }
        delete bucket.swarm_width_lanes; delete bucket.v_dice_multiplier;
    }
    return row;
}

/** finalize_creature_arr's opening window, or the equivalent non-creature
 * definition classification window in query_full_item_arr. */
export function constructClassificationFromBase(base: Row, metadata: FromBaseInventoryMetadata, prefix: Row,
    context = createClassificationContext(base, metadata)): Row {
    const { readers, dynamicClassification, classificationTypes } = context;
    let row = fromBaseClone(prefix);
    const creature = readItemField(row, 'item_type') === 'creature';
    if (creature) {
        const ec = fromBaseRecord(row.encounter_creatures) ? row.encounter_creatures : null;
        const name = readItemField(ec, 'encounter_creature_name'), authored = readItemField(row, 'item_name');
        (row.items as Row).item_name = compileCreatureDisplayName({ playerName: null,
            encounterCreatureName: name == null ? null : phpJsonString(name), itemName: authored == null ? null : phpJsonString(authored) });
        row = applyCreatureMonsterTemplate(row, base, context);
        row = constructCreatureSwarmState(row, base);
        const modelId = readers.layerValue(row, 'creature_model');
        if (fromBaseUuid(modelId)) {
            const model = context.rawDefinition(modelId);
            if (!model || !Array.isArray(model.pictures)) throw new FromBaseInputMissing('creature-model-pictures', modelId);
            const pictures = constructPlayerDefinitionPictures(model, metadata.cdnUrl, readers.layerValue);
            if (pictures.length) {
                row.pictures = { ...(fromBaseRecord(row.pictures) ? row.pictures : {}), item: pictures };
                for (const name of ['item_picture', 'item_pictures', 'item_video', 'item_videos']) delete row[name];
            }
        }
    }
    if (classificationTypes.has(String(readItemField(row, 'item_type')))) {
        row = context.intrinsicEnchantment(dynamicClassification(row));
    }
    if (creature) context.canonicalSize(row);
    return row;
}
